import { RevenueObservationService } from './../../../revenue/shared/revenue-observation.service';
import { InvoiceUpsertDto } from './../../dto/invoice-upsert.dto';
import { InvoiceLineItemUpsertDto } from './../../dto/invoice-line-item-upsert.dto';
import { AdjustmentTypeService } from './../../../revenue/shared/adjustment-types/adjustment-type.service';
import { InvoiceAdjustmentUpsertDto, initializedInvoiceAdjustmentUpsertDto } from './../../dto/invoice-adjustment-upsert.dto';
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AdjustmentType } from 'src/app/revenue/shared/adjustment-types/adjustment-type.model';
import { RevenueItemService } from 'src/app/revenue/shared/revenue-items/revenue-item.service';
import { RevenueItem } from 'src/app/revenue/shared/revenue-items/revenue-item.model';
import { RevenueItemSearchDto } from 'src/app/revenue/shared/revenue-items/dto/revenue-item-search.dto';

@Component({
  selector: 'wl-invoice-adjustment',
  templateUrl: './invoice-adjustment.component.html',
  styleUrls: ['./invoice-adjustment.component.less'],
    standalone: false
})
export class InvoiceAdjustmentComponent implements OnInit, OnChanges {
  @Input() invoice: InvoiceUpsertDto;
  @Input() isLineItemAdjustments: boolean;
  @Input() adjustmentHeader: InvoiceAdjustmentUpsertDto;
  @Input() allRevenueItems: RevenueItem[];

  public adjustmentTypes: Array<AdjustmentType>;
  public flatAdjustmentIds = new Array<number>();
  public addingNewAdjustment = false;
  private _cleanAdjustmentHeader: string;

  disallowPartialPaymentAdjustment = false;

  public get selectedAdjustmentType() {
    return this._adjustmentType;
  }
  public set selectedAdjustmentType(value: AdjustmentType) {
    this._adjustmentType = value;
    this.adjustmentHeader.AdjustmentType = value.AdjustmentType;
    this.adjustmentHeader.AdjustmentTypeId = value.AdjustmentTypeId;
  }

  public get adjustmentSign() {
    if (this._adjustmentIsNegative) {
      return '-';
    } else {
      return '+';
    }
  }

  private _adjustmentType: AdjustmentType;
  private _adjustmentIsNegative = true;

  constructor(
    public revenueObservationService: RevenueObservationService,
    private _adjustmentTypeService: AdjustmentTypeService
  ) {
    this.flatAdjustmentIds = new Array<number>();
  }

  ngOnInit() {
    this._adjustmentTypeService.getAdjustmentTypes().then((data: Array<AdjustmentType>) => {
      this.adjustmentTypes = data;
    });
    this.revenueObservationService.observeRequestInvoiceUpsert().subscribe(() => {
      this.setDirty();
    });
    this.revenueObservationService.observeInvoiceLoaded().subscribe(() => {
      this.setCleanObject();
    });
    this.setCleanObject();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.adjustmentTypes && this.adjustmentTypes.length > 0) {
      this.selectedAdjustmentType = this.adjustmentTypes[0];
    }

    if (this.isLineItemAdjustments) {
      for (const lineItem of this.invoice.LineItems) {
        if (lineItem.Adjustments) {
          for (const adjustment of lineItem.Adjustments) {
            this.flatAdjustmentIds.push(adjustment.AdjustmentId);
          }
        }
      }
    } else {
      for (const lineItem of this.invoice.LineItems) {
        if (lineItem.Payments) {
          for (const payment of lineItem.Payments) {
            if (payment.Adjustments) {
              for (const adjustment of payment.Adjustments) {
                this.flatAdjustmentIds.push(adjustment.AdjustmentId);
              }
            }
          }
        }
      }
    }
  }

  get dirty() {
    return JSON.stringify(this.adjustmentHeader) != this._cleanAdjustmentHeader;
  }

  private setDirty() {
    this.adjustmentHeader.Dirty = this.dirty;
  }

  private setCleanObject() {
    this._cleanAdjustmentHeader = JSON.stringify(this.adjustmentHeader);
  }

  public flipAdjustmentSign() {
    this._adjustmentIsNegative = !this._adjustmentIsNegative;
    this.adjustmentHeader.AdjustmentAmount *= -1;
    this.distributeAdjustmentAmount();
  }

  public formatAdjustmentAmount() {
    if (
      (this._adjustmentIsNegative && this.adjustmentHeader.AdjustmentAmount > 0) ||
      (!this._adjustmentIsNegative && this.adjustmentHeader.AdjustmentAmount < 0)
    ) {
      this.adjustmentHeader.AdjustmentAmount *= -1;
    }
    this.distributeAdjustmentAmount();
  }

  public addAdjustment() {
    this.addingNewAdjustment = true;

    if (!this.isLineItemAdjustments) {
      if (this.invoice.LineItems.some((x) => x.Payments.some((y) => y.CC_CreditCardNum))) {
        const maxDate = this.invoice.LineItems.map((x) =>
          x.Payments.map((y) => y.PaymentDate).reduce((prev, current) => (prev > current ? prev : current))
        ).reduce((prev, current) => (prev > current ? prev : current));
        const minYear = maxDate.getFullYear();
        const minMonth = maxDate.getMonth();
        const minDay = maxDate.getDate() + 1;
        const minHours = maxDate.getHours();
        const minMins = maxDate.getMinutes();
        const minSec = maxDate.getSeconds();

        let minPartialDate: Date = null;
        try {
          minPartialDate = new Date(minYear, minMonth, minDay, minHours, minMins, minSec);
        } catch {
          minPartialDate = new Date(minYear, minMonth + 1, 1, minHours, minMins, minSec);
        }

        this.disallowPartialPaymentAdjustment = minPartialDate >= new Date();

        if (this.disallowPartialPaymentAdjustment) {
          this.adjustmentHeader.AdjustmentAmount =
            -1 *
            this.invoice.LineItems.map((x) => x.Payments.map((y) => y.Amount).reduce((prev, current) => prev + current)).reduce(
              (prev, current) => prev + current
            );

          this.distributeAdjustmentAmount();
        }
      }
    }
  }

  public cancelAdjustment() {
    this.adjustmentHeader = initializedInvoiceAdjustmentUpsertDto();
    if (this.isLineItemAdjustments) {
      this.invoice.LineItemAdjustmentHeader = this.adjustmentHeader;
    } else {
      this.invoice.PaymentAdjustmentHeader = this.adjustmentHeader;
    }
    this.clearNewLineItemAdjustments();
    this.clearNewPaymentAdjustments();
    this.addingNewAdjustment = false;
    this.revenueObservationService.invoiceAmountChanged(this.invoice.InvoiceHeader.InvoiceNum);
  }

  private distributeAdjustmentAmount() {
    if (!this.adjustmentHeader.AdjustmentAmount) {
      return;
    }
    if (this.isLineItemAdjustments) {
      this.distributeLineItemAdjustments();
    } else {
      this.distributePaymentAdjustments();
    }

    this.revenueObservationService.invoiceAmountChanged(this.invoice.InvoiceHeader.InvoiceNum);
  }

  private clearNewLineItemAdjustments() {
    this.invoice.LineItems.forEach((item) => {
      item.AdjustmentAmountToApply = 0;
      item.TaxLineItems.forEach((tli) => {
        tli.AdjustmentAmountToApply = 0;
      });
    });
  }

  private clearNewPaymentAdjustments() {
    this.invoice.LineItems.forEach((lineItem) => {
      lineItem.Payments.forEach((payment) => {
        payment.AdjustmentAmountToApply = 0;
      });
    });
  }

  private distributeLineItemAdjustments() {
    let adjustmentAmount = this.adjustmentHeader.AdjustmentAmount;

    this.clearNewLineItemAdjustments();

    if (!this._adjustmentIsNegative) {
      this.invoice.LineItems[0].AdjustmentAmountToApply = adjustmentAmount;
    } else {
      this.invoice.LineItems.forEach((lineItem) => {
        let adjLIAmt = lineItem.Amount;
        if (lineItem.Adjustments && lineItem.Adjustments.length > 0) {
          adjLIAmt += lineItem.Adjustments.map((x) => x.AdjustmentAmount).reduce((a, b) => a + b);
        }
        if (adjustmentAmount + adjLIAmt >= 0) {
          lineItem.AdjustmentAmountToApply = adjustmentAmount;
          adjustmentAmount = 0;
          return;
        } else {
          lineItem.AdjustmentAmountToApply = -1 * adjLIAmt;
          adjustmentAmount += adjLIAmt;
        }
        lineItem.TaxLineItems.forEach((taxLineItem) => {
          let adjTLIAmt = taxLineItem.Amount;
          if (taxLineItem.Adjustments && taxLineItem.Adjustments.length > 0) {
            adjTLIAmt += taxLineItem.Adjustments.map((x) => x.AdjustmentAmount).reduce((a, b) => a + b);
          }
          if (adjustmentAmount + adjTLIAmt >= 0) {
            taxLineItem.AdjustmentAmountToApply = adjustmentAmount;
            adjustmentAmount = 0;
            return;
          } else {
            taxLineItem.AdjustmentAmountToApply = -1 * adjTLIAmt;
            adjustmentAmount += adjTLIAmt;
          }
        });
      });

      // if we only auto-applied part of the total due to existing adjustments, go ahead and set the total back to what was applied
      let totalAdj = 0;
      for (const lineItem of this.invoice.LineItems) {
        totalAdj += lineItem.AdjustmentAmountToApply;
        if (lineItem.TaxLineItems.length > 0) {
          totalAdj += lineItem.TaxLineItems.map((x) => x.AdjustmentAmountToApply).reduce((a, b) => a + b);
        }
      }
      if (this.adjustmentHeader.AdjustmentAmount !== totalAdj) {
        this.adjustmentHeader.AdjustmentAmount = totalAdj;
      }
    }
  }

  private distributePaymentAdjustments() {
    let adjustmentAmount = this.adjustmentHeader.AdjustmentAmount;

    this.invoice.LineItems.forEach((item) => {
      item.Payments.forEach((payment) => {
        payment.AdjustmentAmountToApply = 0;
      });
    });

    if (!this._adjustmentIsNegative) {
      this.invoice.LineItems[0].Payments[0].AdjustmentAmountToApply = adjustmentAmount;
    } else {
      this.invoice.LineItems.forEach((lineItem) => {
        lineItem.Payments.forEach((payment) => {
          let adjPayAmt = payment.Amount;
          if (payment.Adjustments && payment.Adjustments.length > 0) {
            adjPayAmt += payment.Adjustments.map((x) => x.AdjustmentAmount).reduce((a, b) => a + b);
          }
          if (adjustmentAmount + adjPayAmt >= 0) {
            payment.AdjustmentAmountToApply = adjustmentAmount;
            adjustmentAmount = 0;
            return;
          } else {
            payment.AdjustmentAmountToApply = -1 * adjPayAmt;
            adjustmentAmount += adjPayAmt;
          }
        });
      });

      // if we only auto-applied part of the total due to existing adjustments, go ahead and set the total back to what was applied
      let totalAdj = 0;
      for (const lineItem of this.invoice.LineItems) {
        if (lineItem.Payments.length > 0) {
          totalAdj += lineItem.Payments.map((x) => x.AdjustmentAmountToApply).reduce((a, b) => a + b);
        }
      }
      if (this.adjustmentHeader.AdjustmentAmount !== totalAdj) {
        this.adjustmentHeader.AdjustmentAmount = totalAdj;
      }
    }
  }

  getItemDescription(itemNum: number) {
    const revItem = this.allRevenueItems.find((x) => x.ItemNum === itemNum);
    if (revItem) {
      return revItem.Descr;
    } else {
      return '';
    }
  }
}
